import { useEffect, useRef, useState } from 'react';
import { FiCalendar } from 'react-icons/fi';
import styles from './RangeDatePicker.module.css';

const MONTHS_PT = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];
const DAYS_PT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
// Mesmo corte do CSS (RangeDatePicker.module.css).
const MOBILE_QUERY = '(max-width: 640px)';

const toStr = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const fromStr = (s: string) => {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
};

const fmt = (s: string) => {
    const [y, m, d] = s.split('-');
    return `${d}/${m}/${y}`;
};

const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

function getDays(year: number, month: number) {
    const firstDay = new Date(year, month, 1).getDay();
    const lastDay = new Date(year, month + 1, 0).getDate();
    const cells: Array<{ date: Date; isCurrentMonth: boolean }> = [];

    for (let i = firstDay - 1; i >= 0; i--) {
        cells.push({ date: new Date(year, month, -i), isCurrentMonth: false });
    }
    for (let d = 1; d <= lastDay; d++) {
        cells.push({ date: new Date(year, month, d), isCurrentMonth: true });
    }
    const remaining = 7 - (cells.length % 7);
    if (remaining < 7) {
        for (let d = 1; d <= remaining; d++) {
            cells.push({ date: new Date(year, month + 1, d), isCurrentMonth: false });
        }
    }
    return cells;
}

export interface RangeDatePickerProps {
    onChange: (start: string, end: string) => void;
    onClear: () => void;
    maxDate?: string;
    initialStart?: string;
    initialEnd?: string;
}

export function RangeDatePicker({ onChange, onClear, maxDate, initialStart, initialEnd }: RangeDatePickerProps) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxD = maxDate ? fromStr(maxDate) : null;

    const [start, setStart] = useState(initialStart ?? '');
    const [end, setEnd] = useState(initialEnd ?? '');
    const [isOpen, setIsOpen] = useState(false);
    const [popupAlign, setPopupAlign] = useState<'left' | 'right'>('left');

    useEffect(() => {
        if (initialStart && initialEnd) onChange(initialStart, initialEnd);
    }, []);
    const [activeField, setActiveField] = useState<'start' | 'end'>('start');
    const [hover, setHover] = useState<Date | null>(null);
    const [leftMonth, setLeftMonth] = useState(() => {
        if (initialStart) {
            const d = fromStr(initialStart);
            return new Date(d.getFullYear(), d.getMonth(), 1);
        }
        return new Date(today.getFullYear(), today.getMonth() - 1, 1);
    });
    const rightMonth = new Date(leftMonth.getFullYear(), leftMonth.getMonth() + 1, 1);
    const wrapperRef = useRef<HTMLDivElement>(null);

    // No celular o popup vira uma folha presa embaixo da tela com um mes so;
    // dois meses lado a lado (ou empilhados) nao cabem e vazavam pela lateral.
    const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
    useEffect(() => {
        const mq = window.matchMedia(MOBILE_QUERY);
        const onChange = () => setIsMobile(mq.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    const computeAlign = () => {
        if (!wrapperRef.current) return;
        const rect = wrapperRef.current.getBoundingClientRect();
        const spaceRight = window.innerWidth - rect.left;
        setPopupAlign(spaceRight >= 600 ? 'left' : 'right');
    };

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setIsOpen(false);
                setHover(null);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const openWith = (field: 'start' | 'end') => {
        computeAlign();
        setActiveField(field);
        setIsOpen(true);
        if (field === 'start' && start) {
            const d = fromStr(start);
            setLeftMonth(new Date(d.getFullYear(), d.getMonth() - 1, 1));
        } else if (field === 'end' && end) {
            const d = fromStr(end);
            setLeftMonth(new Date(d.getFullYear(), d.getMonth() - 1, 1));
        }
    };

    const handleDayClick = (date: Date, isCurrentMonth: boolean) => {
        if (!isCurrentMonth || (maxD !== null && date > maxD)) return;
        const ds = toStr(date);

        if (activeField === 'start') {
            const newEnd = end && ds > end ? '' : end;
            setStart(ds);
            setEnd(newEnd);
            setActiveField('end');
            if (newEnd) {
                onChange(ds, newEnd);
                setIsOpen(false);
                setHover(null);
            }
        } else {
            if (start && ds < start) {
                setStart(ds);
                setEnd('');
                setActiveField('end');
            } else {
                const newStart = start || ds;
                setEnd(ds);
                onChange(newStart, ds);
                setIsOpen(false);
                setHover(null);
            }
        }
    };

    const handleClear = () => {
        setStart('');
        setEnd('');
        setActiveField('start');
        setIsOpen(false);
        onClear();
    };

    const goToday = () => {
        const ds = toStr(today);
        if (activeField === 'start') {
            const newEnd = end && ds > end ? '' : end;
            setStart(ds);
            setEnd(newEnd);
            setActiveField('end');
            if (newEnd) {
                onChange(ds, newEnd);
                setIsOpen(false);
                setHover(null);
            }
        } else {
            const newStart = start || ds;
            setEnd(ds);
            onChange(newStart, ds);
            setIsOpen(false);
            setHover(null);
        }
    };

    const prevMonth = () =>
        setLeftMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1));
    const nextMonth = () =>
        setLeftMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1));

    const renderMonth = (year: number, month: number, showPrev: boolean, showNext: boolean) => {
        const cells = getDays(year, month);
        const startD = start ? fromStr(start) : null;
        const endD = end ? fromStr(end) : null;
        const rangeEnd = endD ?? (activeField === 'end' && hover && startD && hover >= startD ? hover : null);

        return (
            <div className={styles.monthPanel} key={`${year}-${month}`}>
                <div className={styles.monthHeader}>
                    {showPrev
                        ? <button type="button" className={styles.navBtn} onClick={prevMonth} aria-label="Mês anterior">&#8249;</button>
                        : <div className={styles.navSpacer} />
                    }
                    <span className={styles.monthTitle}>{MONTHS_PT[month]} {year}</span>
                    {showNext
                        ? <button type="button" className={styles.navBtn} onClick={nextMonth} aria-label="Próximo mês">&#8250;</button>
                        : <div className={styles.navSpacer} />
                    }
                </div>

                <div className={styles.dayHeaderRow}>
                    {DAYS_PT.map(d => (
                        <span key={d} className={styles.dayHeaderCell}>{d}</span>
                    ))}
                </div>

                <div className={styles.dayGrid}>
                    {cells.map(({ date, isCurrentMonth }, idx) => {
                        const isStart = !!(startD && isCurrentMonth && sameDay(date, startD));
                        const isEnd = !!(rangeEnd && isCurrentMonth && sameDay(date, rangeEnd));
                        const inRange = !!(startD && rangeEnd && date > startD && date < rangeEnd && isCurrentMonth);
                        const isDisabled = isCurrentMonth && maxD !== null && date > maxD;
                        const isTodayCell = isCurrentMonth && sameDay(date, today);

                        const cls = [
                            styles.dayCell,
                            !isCurrentMonth ? styles.otherMonth : '',
                            isDisabled ? styles.disabled : '',
                            isTodayCell ? styles.isToday : '',
                            inRange ? styles.inRange : '',
                            isStart ? styles.isStart : '',
                            isEnd ? styles.isEnd : '',
                        ].filter(Boolean).join(' ');

                        return (
                            <button
                                key={idx}
                                type="button"
                                className={cls}
                                disabled={isDisabled}
                                onClick={() => handleDayClick(date, isCurrentMonth)}
                                onMouseEnter={() => isCurrentMonth && setHover(date)}
                                onMouseLeave={() => setHover(null)}
                            >
                                {date.getDate()}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className={styles.wrapper} ref={wrapperRef}>
            <div className={styles.triggerRow}>
                <button
                    type="button"
                    className={`${styles.triggerField}${isOpen && activeField === 'start' ? ` ${styles.active}` : ''}`}
                    onClick={() => openWith('start')}
                >
                    <FiCalendar size={13} className={styles.triggerIcon} />
                    <span>
                        <span className={styles.triggerLabel}>Início</span>
                        <span className={styles.triggerValue}>{start ? fmt(start) : 'dd/mm/aaaa'}</span>
                    </span>
                </button>
                <div className={styles.triggerDivider} />
                <button
                    type="button"
                    className={`${styles.triggerField}${isOpen && activeField === 'end' ? ` ${styles.active}` : ''}`}
                    onClick={() => openWith('end')}
                >
                    <FiCalendar size={13} className={styles.triggerIcon} />
                    <span>
                        <span className={styles.triggerLabel}>Fim</span>
                        <span className={styles.triggerValue}>{end ? fmt(end) : 'dd/mm/aaaa'}</span>
                    </span>
                </button>
            </div>

            {isOpen && isMobile && (
                <div
                    className={styles.backdrop}
                    onClick={() => {
                        setIsOpen(false);
                        setHover(null);
                    }}
                />
            )}

            {isOpen && (
                <div
                    className={isMobile ? `${styles.popup} ${styles.popupMobile}` : styles.popup}
                    style={isMobile ? undefined : popupAlign === 'right' ? { right: 0, left: 'auto' } : { left: 0, right: 'auto' }}
                >
                    <div className={styles.monthsRow}>
                        {isMobile ? (
                            // O mes da direita e o da data escolhida (ou o atual): e ele que aparece sozinho.
                            renderMonth(rightMonth.getFullYear(), rightMonth.getMonth(), true, true)
                        ) : (
                            <>
                                {renderMonth(leftMonth.getFullYear(), leftMonth.getMonth(), true, false)}
                                {renderMonth(rightMonth.getFullYear(), rightMonth.getMonth(), false, true)}
                            </>
                        )}
                    </div>
                    <div className={styles.popupFooter}>
                        <button type="button" className={styles.footerBtn} onClick={handleClear}>
                            Limpar
                        </button>
                        <button type="button" className={styles.footerBtn} onClick={goToday}>
                            Hoje
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
